import { Container, SkeletonText, Table } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { lazy, useEffect, useMemo, useState } from "react"
import { z } from "zod"

import { type TagsOrderBy } from "../../client"
import ActionsMenu from "../../components/Common/ActionsMenu"
import Navbar from "../../components/Common/Navbar"
import { PaginationFooter } from "../../components/Common/PaginationFooter.tsx"
import Sort from "../../components/Common/Sort"
import TitleBar from "../../components/UI/TitleBar"
import { userHasPermission } from "../../utils.ts"
import { NoPermission } from "../../components/UI/no-permission"
import { readTags, TagsTableProps } from "../../api/tags.ts"

const AddTag = lazy(() => import("../../components/Tags/AddTag"))
const PER_PAGE = 25

const tagsSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/tags")({
  component: Tags,
  validateSearch: (search) => tagsSearchSchema.parse(search),
})


function TagsTable({ searchValue, tg_update, tg_delete }: TagsTableProps) {
  const fields = [
    { title: "Name", col: "name", align: undefined },
    { title: "Anzahl", col: null, align: undefined },
    { title: "Erstellungsdatum", col: "created_at", align: undefined },
    { title: "Bearbeitungsdatum", col: "edited_at", align: undefined },
    { title: "ID", col: null, align: undefined },
    { title: "Aktionen", col: null, align: "right" as const },
  ]

  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setPage = (page: number) =>
    navigate({
      search: (prev: { [key: string]: string }) => ({ ...prev, page }),
    })

  // Set page on search value change
  const [prevPage, setPrevPage] = useState<number>(page)
  useEffect(() => {
    if(searchValue == "") {
      setPage(prevPage)
    } else {
      setPrevPage(page)
      setPage(1)
    }
  }, [searchValue])

  // Sorting
  const [columnName, setColumnName] = useState<TagsOrderBy>()
  const [orderBy, setOrderBy] = useState<string>("")
  const [isDesc, setIsDesc] = useState<boolean>()

  // Query
  const queryOptions = useMemo(() =>
    readTags({
      per_page: PER_PAGE,
      page: page,
      order_by: columnName,
      descending: isDesc,
      query: searchValue,
    }),
  [page, columnName, isDesc, searchValue])

  const {
    data: tags,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...queryOptions,
    placeholderData: (prevData) => prevData,
  })

  // Sorting
  const sortTable = (name: TagsOrderBy) => {
    if (columnName !== name) {
      setColumnName(name)
      setOrderBy("")
      sorting("")
    } else {
      sorting(orderBy)
    }
  }

  const sorting = (order: string) => {
    switch (order) {
      case "":
        setOrderBy("desc")
        setIsDesc(true)
        break
      case "desc":
        setOrderBy("asc")
        setIsDesc(false)
        break
      case "asc":
        setOrderBy("")
        setIsDesc(undefined)
        setColumnName(undefined)
        break
    }
  }

  return (
    <>
      <Table.ScrollArea asChild>
        <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
          <Table.Header>
            <Table.Row>
              {fields.map((field, i) =>
                field.col != null ? (
                  <Table.ColumnHeader
                    onClick={() => sortTable(field.col as TagsOrderBy)}
                    cursor="pointer"
                    textAlign={field.align}
                    key={i}
                  >
                    {field.title}
                    {columnName === field.col &&
                      <Sort
                        columnNamePassed={columnName}
                        orderByPassed={orderBy}
                      />
                    }
                  </Table.ColumnHeader>
                ) : (
                  <Table.ColumnHeader textAlign={field.align} key={field.title}>
                    {field.title}
                  </Table.ColumnHeader>
                ),
              )}
            </Table.Row>
          </Table.Header>
          {isPending ? (
            <Table.Body>
              <Table.Row>
                {new Array(6).fill(null).map((_, index) => (
                  <Table.Cell key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          ) : (
            <Table.Body>
              {!tags?.data ? (
                <Table.Row>
                  <Table.Cell colSpan={6} textAlign="center">
                    Keine Tags verfügbar
                  </Table.Cell>
                </Table.Row>
              ) : (
                tags?.data.map((tag) => (
                  <Table.Row key={tag.id} opacity={isPlaceholderData ? 0.5 : 1}>
                    <Table.Cell>{tag.name}</Table.Cell>

                    <Table.Cell>{tag.publication_count}</Table.Cell>

                    <Table.Cell>
                      {new Date(`${tag.created_at}`).toLocaleString("de-CH", {
                        timeZone: "Europe/Zurich",
                      })}
                    </Table.Cell>

                    <Table.Cell>
                      {tag.edited_at && 
                        new Date(`${tag.edited_at}`).toLocaleString("de-CH", {
                          timeZone: "Europe/Zurich",
                        })
                      }
                    </Table.Cell>

                    <Table.Cell>{tag.id}</Table.Cell>

                    <Table.Cell textAlign="right">
                      <ActionsMenu
                        type="Tag"
                        value={tag}
                        disableDelete={tag.publication_count > 0 || !tg_delete}
                        disableEdit={!tg_update}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          )}
        </Table.Root>
      </Table.ScrollArea>
      <PaginationFooter
        onChangePage={setPage}
        page={page}
        count={tags?.count ? tags?.count : 0}
        perPage={PER_PAGE}
      />
    </>
  )
}

function Tags() {
  // Permission check
  const [tg_create, tg_read, tg_update, tg_delete] = useMemo(() => {
    return ["create", "read", "update", "delete"].map(
      (action) => userHasPermission([{ perm: `tags_${action}`, is_allowed: false }])[0].is_allowed
    )
  }, [userHasPermission])

  if(!tg_read) return <NoPermission/>

  const [searchValue, setSearchValue] = useState<string>("")

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value)
  }

  return (
    <Container maxW="full">
      <TitleBar title="Tags" />
      <Navbar
        type="Tag"
        addAllowed={tg_create}
        addModalAs={AddTag}
        showSearch={true}
        onSearchChange={handleSearchValueChange}
      />
      <TagsTable
        searchValue={searchValue}
        tg_update={tg_update}
        tg_delete={tg_delete}
      />
    </Container>
  )
}
